import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import {
  fetchTournamentSignupSettings,
  getDefaultSignupSettings,
} from "../../utility/signupSettings";
import { occupancyBySquadId } from "../../utility/signupOccupancy";
import { fetchDoublesSinglesSetup } from "../../utility/doublesSinglesSetupApi";
import {
  eventsBySquadFromRows,
  onlineSignupHasOpenPath,
  SIGNUP_BANNER,
  signupBannerState,
} from "../../utility/signupAvailability";
import { tournamentPath } from "../../utility/tournamentUrls";
import { useTournamentSlug } from "../../context/TournamentRouteContext";

const SignupPrompt = ({ tournamentId }) => {
  const tournamentSlug = useTournamentSlug();
  const [banner, setBanner] = useState(SIGNUP_BANNER.none);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const settings =
          (await fetchTournamentSignupSettings(tournamentId).catch(() =>
            getDefaultSignupSettings(tournamentId)
          )) || getDefaultSignupSettings(tournamentId);

        const { data: events } = await supabase
          .from("tournamentEvents")
          .select("id, eventName, bowlerQuantity, isPrimary")
          .eq("tournamentID", tournamentId)
          .eq("isPrimary", true)
          .eq("isDeleted", false);

        const { data: squads } = await supabase
          .from("squads")
          .select(
            `
            *,
            bowlingCenters ( stateShort )
          `
          )
          .eq("tournamentID", tournamentId)
          .eq("isDeleted", false);

        let occupancyBySquad = {};
        let occupancyKnown = true;
        let eventsBySquad = {};
        if (squads?.length) {
          const ids = squads.map((squad) => squad.id);
          const { data: occupancyRows, error: occupancyError } =
            await supabase.rpc("get_squad_occupancy", { p_squad_ids: ids });
          if (occupancyError) {
            occupancyKnown = false;
          } else {
            occupancyBySquad = occupancyBySquadId(occupancyRows || []);
          }
          const { data: eventRows, error: eventError } = await supabase
            .from("squadEvents")
            .select("squadID, eventsID")
            .in("squadID", ids);
          if (!eventError) {
            eventsBySquad = eventsBySquadFromRows(eventRows || []);
          }
        }

        const { data: verificationRows, error: verificationError } =
          await supabase
            .from("verificationStates")
            .select("isVerified")
            .eq("tournamentId", tournamentId)
            .eq("entityType", "results");
        const resultsVerified =
          !verificationError &&
          (verificationRows || []).some((row) => row.isVerified === true);

        const doublesSingles = await fetchDoublesSinglesSetup(
          tournamentId
        ).catch(() => null);

        if (cancelled) return;
        const liveSquads = squads || [];
        const hasOpenPath = occupancyKnown
          ? onlineSignupHasOpenPath(events || [], liveSquads, {
              events: events || [],
              eventsBySquad,
              doublesSingles,
              occupancyBySquad,
              waitlistEnabled: Boolean(settings.waitlistEnabled),
              settings,
              squads: liveSquads,
            })
          : false;
        setBanner(
          signupBannerState({
            resultsVerified,
            squads: liveSquads,
            settings,
            hasOpenPath,
            occupancyKnown,
          })
        );
      } catch (error) {
        console.error("Error fetching signup settings:", error);
        if (!cancelled) setBanner(SIGNUP_BANNER.closed);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  if (loading || banner === SIGNUP_BANNER.none) {
    return null;
  }

  if (banner === SIGNUP_BANNER.closed) {
    return (
      <div className="card-board border-l-4 border-l-brand-charcoal/30 p-4">
        <h3 className="text-sm font-display font-bold text-brand-charcoal">
          Online signup is closed
        </h3>
        <p className="text-sm text-brand-muted mt-1">
          New online entries are not being accepted. The tournament may still
          be bowling or posting results.
        </p>
      </div>
    );
  }

  return (
    <div className="card-board border-l-4 border-l-brand-gold-ink p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-display font-bold text-brand-charcoal">
            Sign up
          </h3>
          <p className="text-sm text-brand-muted mt-1">
            Choose a squad and enter this tournament.
          </p>
        </div>
        <Link
          to={tournamentPath(tournamentSlug, "/signups")}
          className="btn-brand text-sm whitespace-nowrap flex-shrink-0"
        >
          Sign up
        </Link>
      </div>
    </div>
  );
};

export default SignupPrompt;
